import {
	AlertCircle,
	ChevronDown,
	ChevronRight,
	ExternalLink,
	FolderGit2,
	Loader2,
} from "lucide-react";
import React, { useMemo } from "react";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { sortStackedMergeRequests } from "../../stackedMergeRequests";
import { formatProjectName, safeHttpUrl } from "../../utils";
import { ReviewBoardCard } from "../ReviewBoardCard";
import { LaneStats, collectOpenMRs, createSorter } from "./laneParts";

const RepoColumn = ({
	lane,
	collapsed,
	onToggle,
	greyOutBlockingComments,
	enableCommentsExpansion = false,
}) => {
	const repoName = formatProjectName(lane.repo);

	// Collapsed: a narrow vertical strip showing the repo name and count.
	if (collapsed) {
		return (
			<div className="flex flex-col flex-shrink-0 w-12">
				<button
					type="button"
					onClick={onToggle}
					className="flex flex-col items-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-secondary)]/50 py-2 h-full hover:bg-[var(--color-bg-secondary)] transition-colors"
					aria-expanded={!collapsed}
					title={repoName}
				>
					<ChevronRight className="w-4 h-4 text-[var(--color-text-secondary)]" />
					<span className="text-xs font-medium px-1.5 py-0.5 rounded-full bg-[var(--color-bg-secondary)] border border-[var(--color-border)] text-[var(--color-text-secondary)]">
						{lane.mrs.length}
					</span>
					<span
						className="text-xs font-semibold text-[var(--color-text-primary)] truncate max-h-[60vh]"
						style={{ writingMode: "vertical-rl", textOrientation: "mixed" }}
					>
						{repoName}
					</span>
				</button>
			</div>
		);
	}

	return (
		<div className="flex flex-col flex-shrink-0 w-96 max-h-[70vh]">
			{/* Column header */}
			<div className="rounded-t-lg border border-b-0 border-[var(--color-border)] bg-[var(--color-bg-secondary)]/50 px-3 py-2">
				<div className="flex items-center justify-between gap-2">
					<button
						type="button"
						onClick={onToggle}
						className="inline-flex items-center gap-2 min-w-0 font-semibold text-[var(--color-text-primary)] hover:text-[var(--color-text-secondary)] transition-colors"
						aria-expanded={!collapsed}
					>
						<ChevronDown className="w-4 h-4 flex-shrink-0 text-[var(--color-text-secondary)]" />
						<FolderGit2 className="w-4 h-4 flex-shrink-0 text-[var(--color-text-secondary)]" />
						<span className="truncate">{repoName}</span>
						<span className="flex-shrink-0 text-xs font-medium px-2 py-0.5 rounded-full bg-[var(--color-bg-secondary)] border border-[var(--color-border)] text-[var(--color-text-secondary)]">
							{lane.mrs.length}
						</span>
					</button>
					{safeHttpUrl(lane.repoUrl) && (
						<a
							href={safeHttpUrl(lane.repoUrl)}
							target="_blank"
							rel="noopener noreferrer"
							className="p-1 rounded-md flex-shrink-0 text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)] transition-colors"
							title="Open repository in GitLab"
						>
							<ExternalLink className="w-4 h-4" />
						</a>
					)}
				</div>

				<LaneStats mrs={lane.mrs} className="mt-2" />
			</div>

			{/* Column body: vertically stacked, scrollable */}
			<div className="flex-1 overflow-y-auto rounded-b-lg border border-t-0 border-[var(--color-border)] bg-[var(--color-bg-primary)] p-2">
				{lane.mrs.map((mr) => (
					<ReviewBoardCard
						key={mr.id}
						mr={mr}
						enableDrag={false}
						greyOutBlockingComments={greyOutBlockingComments}
						enableCommentsExpansion={enableCommentsExpansion}
					/>
				))}
			</div>
		</div>
	);
};

/**
 * Groups open merge requests by repository, rendering one vertical column per
 * repo (kanban-style), with columns scrolling horizontally. A repo only appears
 * when it has at least one open MR.
 */
export const RepoView = ({
	pendingReviewMRs,
	myOpenMRs,
	loading,
	error,
	sortOption = "updatedAt",
	greyOutBlockingComments = true,
	enableCommentsExpansion = false,
}) => {
	const [collapsedRepos, setCollapsedRepos] = useLocalStorage(
		"repoview-collapsed",
		{},
	);

	const lanes = useMemo(() => {
		const openMRs = collectOpenMRs(pendingReviewMRs, myOpenMRs);

		// Group by repo path.
		const groups = new Map();
		for (const mr of openMRs) {
			const repo = mr.project?.fullPath || mr.projectPath || "Unknown";
			if (!groups.has(repo)) {
				groups.set(repo, {
					repo,
					repoUrl: mr.webUrl?.split("/-/")[0],
					mrs: [],
				});
			}
			groups.get(repo).mrs.push(mr);
		}

		const sorter = createSorter(sortOption);
		const result = [...groups.values()];
		for (const lane of result) {
			lane.mrs = sortStackedMergeRequests(lane.mrs, sorter);
		}

		// Lanes with the most open MRs first, then alphabetical.
		result.sort(
			(a, b) => b.mrs.length - a.mrs.length || a.repo.localeCompare(b.repo),
		);
		return result;
	}, [pendingReviewMRs, myOpenMRs, sortOption]);

	const toggleRepo = (repo) => {
		setCollapsedRepos((prev) => ({ ...prev, [repo]: !prev[repo] }));
	};

	if (loading) {
		return (
			<div className="flex items-center justify-center h-[calc(100vh-200px)]">
				<div className="text-center">
					<Loader2 className="w-12 h-12 mx-auto text-[var(--color-text-secondary)] animate-spin mb-4" />
					<p className="text-[var(--color-text-secondary)]">
						Loading merge requests...
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="w-full">
			{error && (
				<div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-lg flex items-center gap-2 text-red-800 dark:text-red-200">
					<AlertCircle className="w-5 h-5 flex-shrink-0" />
					<span className="text-sm">{error}</span>
				</div>
			)}

			{lanes.length === 0 ? (
				<div className="bg-[var(--color-info-bg)] text-[var(--color-info-text)] px-4 py-3 rounded">
					No open merge requests
				</div>
			) : (
				<div className="flex flex-wrap gap-4 pb-4 items-start">
					{lanes.map((lane) => (
						<RepoColumn
							key={lane.repo}
							lane={lane}
							collapsed={!!collapsedRepos[lane.repo]}
							onToggle={() => toggleRepo(lane.repo)}
							greyOutBlockingComments={greyOutBlockingComments}
							enableCommentsExpansion={enableCommentsExpansion}
						/>
					))}
				</div>
			)}
		</div>
	);
};
